import { useState, useEffect } from "react";
import api from "../../api/client";

export default function EditAccountModal({ user: target, onClose, onRefresh }) {
  const [form, setForm] = useState({
    display_name: target.display_name || "",
    full_name: target.full_name || "",
    company_id: target.company_id || "",
    assigned_tl_id: target.assigned_tl_id || "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [companies, setCompanies] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);

  const isSalesAgentTL = target.role_name === "Sales Agent/TL" || target.role_name === "Sales Agent";

  useEffect(() => {
    Promise.all([
      api.get("/companies/active").catch(() => ({ data: [] })),
      api.get("/team-leaders/active").catch(() => ({ data: [] })),
    ]).then(([companiesRes, tlRes]) => {
      setCompanies(companiesRes.data);
      setTeamLeaders(tlRes.data);
    });
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setApiError("");
  };

  const validate = () => {
    const errs = {};
    if (!form.display_name.trim()) errs.display_name = "Display name is required.";
    if (!form.company_id) errs.company_id = "Company is required.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    setApiError("");
    try {
      await api.patch(`/users/${target.id}`, {
        display_name: form.display_name,
        full_name: form.full_name || "",
        company_id: form.company_id ? parseInt(form.company_id) : null,
        assigned_tl_id: isSalesAgentTL && form.assigned_tl_id ? parseInt(form.assigned_tl_id) : 0,
      });
      onRefresh();
      onClose();
    } catch (err) {
      setApiError(err.response?.data?.detail || "Failed to update account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b bg-gray-50 rounded-t-xl flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Edit Account</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {target.display_name || target.full_name} &middot; {target.email}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none p-1">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {apiError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
              {apiError}
            </div>
          )}

          {/* Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
            <select
              value={form.company_id}
              onChange={(e) => handleChange("company_id", e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent ${errors.company_id ? "border-red-400" : "border-gray-300"}`}
            >
              <option value="" disabled>Select a company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {errors.company_id && <p className="text-red-500 text-xs mt-1">{errors.company_id}</p>}
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => handleChange("display_name", e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent ${errors.display_name ? "border-red-400" : "border-gray-300"}`}
              placeholder="e.g. Juan DC"
            />
            {errors.display_name && <p className="text-red-500 text-xs mt-1">{errors.display_name}</p>}
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => handleChange("full_name", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              placeholder="e.g. Juan Dela Cruz (optional)"
            />
          </div>

          {/* Role (read-only display) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600">
              {target.role_name || target.role}
              <span className="text-xs text-gray-400 ml-2">(use role assignment to change)</span>
            </div>
          </div>

          {/* Username (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600 font-mono">
              {target.email}
            </div>
          </div>

          {/* Assigned TL — only for Sales Agent/TL */}
          {isSalesAgentTL && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <label className="block text-sm font-medium text-amber-800 mb-1">Assigned Team Leader</label>
              <select
                value={form.assigned_tl_id}
                onChange={(e) => handleChange("assigned_tl_id", e.target.value)}
                className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="">None</option>
                {teamLeaders.map((tl) => (
                  <option key={tl.id} value={tl.id}>{tl.name}</option>
                ))}
              </select>
              <p className="text-xs text-amber-600 mt-1">Select the Team Leader assigned to this user.</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
            >
              {submitting ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
